import { billedOf } from "./figures";
import { billedLessDiscount, type InvoiceAggregateDTO } from "./reporting";

/**
 * The client-margin arithmetic, ported line-for-line from
 * `InvoiceAggregateDTO`'s `@JsonIgnore` getters — they never reach the wire, so
 * the browser has to reproduce them. Any change to the Java formulas must land
 * here too; the names match one-to-one to keep that diff trivial.
 */

/** Refund lines against these part ids are physical returns, not programs. */
const RETURN_PART_IDS = new Set([273, 276]);

export interface MarginBreakdown {
  units: number;
  sales: number;
  discounts: number;
  /** Physical returns (the two return-part SKUs). */
  returns: number;
  /** Everything else refunded: co-op programs, allowances. */
  programs: number;
  totalPrograms: number;
  shippingCost: number;
  advertisingCost: number;
  advertisingSales: number;
  commissions: number;
  royalties: number;
  /** In the client's own currency (the wire carries COGS in CAD). */
  cogs: number;
  totalCost: number;
  grossMargin: number;
  netMargin: number;
  netMarginPercent: number;
}

export function marginBreakdown(aggregate: InvoiceAggregateDTO, fxRate: number): MarginBreakdown {
  const sales = aggregate.sales ?? null;
  const cost = aggregate.cost ?? {};
  const gross = billedOf(sales);
  const pct = (value: number) => (gross === 0 ? 0 : value / gross);

  const discounts = sales?.discount ?? 0;
  const refundsAmount = billedLessDiscount(aggregate.refunds);

  const returns = (aggregate.parts ?? [])
    .filter(
      (part) =>
        part.totals?.order_type === 2 && RETURN_PART_IDS.has(part.part_id ?? part.part?.id ?? -1),
    )
    .reduce((sum, part) => sum + billedLessDiscount(part.totals), 0);

  const programs = refundsAmount - returns;

  const shippingCost = cost.shipping ?? 0;
  const advertisingCost = cost.advertising ?? 0;
  const advertisingSales = cost.advertising_sales ?? 0;

  const commissionsGross = sales?.commission ?? 0;
  const commissions =
    commissionsGross -
    commissionsGross *
      (pct(discounts) + pct(refundsAmount) + pct(shippingCost) + pct(advertisingSales));

  const royaltiesGross = (aggregate.parts ?? []).reduce(
    (sum, part) => sum + (part.cost?.royalty ?? 0),
    0,
  );
  // No discounts in this pool: they are already off each product's royalty
  // base, and were being counted a second time here until 2026-09-11. Mirrors
  // InvoiceAggregateDTO.getRoyaltiesCostsPercent exactly.
  const royaltiesCostsPercent = pct(refundsAmount + shippingCost + advertisingCost + commissions);
  const royalties = royaltiesGross - royaltiesGross * royaltiesCostsPercent;

  // COGS is summed over order-type-1 lines and carried in CAD; dividing by the
  // client's FX rate expresses it in their currency, as getCosts(fx) does.
  const cogsCad = (aggregate.parts ?? [])
    .filter((part) => part.totals?.order_type === 1)
    .reduce((sum, part) => sum + (part.cost?.cogs ?? 0), 0);
  const cogs = fxRate === 0 ? cogsCad : cogsCad / fxRate;

  const totalCost =
    cogs + refundsAmount + discounts + shippingCost + advertisingCost + commissions + royalties;

  const netMargin = gross - totalCost;

  return {
    // Billed units, matching every other unit figure in the app: what left
    // the building. Until 2026-09-11 this subtracted returned units, which put
    // net units beside billed money on the margin page — the same mismatched
    // pair the dashboard had.
    units: sales?.quantity ?? 0,
    sales: gross,
    discounts,
    returns,
    programs,
    totalPrograms: refundsAmount,
    shippingCost,
    advertisingCost,
    advertisingSales,
    commissions,
    royalties,
    cogs,
    totalCost,
    grossMargin: gross - cogs,
    netMargin,
    netMarginPercent: gross === 0 ? 0 : netMargin / gross,
  };
}
